import React, {
  createContext,
  useContext,
  ReactNode,
  ElementType,
  useRef,
  useEffect,
  useCallback,
  useState,
} from "react";
import { Link, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { clearSearchTriggeredModule } from "../../store/layouts/reducer";

interface DropdownContextType {
  toggleOpen: (ref: any) => void;
  setActiveDropdown: React.Dispatch<React.SetStateAction<HTMLElement | null>>;
  highlightedSubModuleId: string | null;
  setHighlightedSubModuleId: React.Dispatch<React.SetStateAction<string | null>>;
}

const DropDownContext = createContext<DropdownContextType | undefined>(undefined);

interface DropdownProps {
  children?: ReactNode;
  as?: ElementType;
  className?: string;
  active?: any;
  id?: any;
  subitemId?: any;
}

const Dropdown = ({ as: Component = "div", children, className, id, subitemId }: DropdownProps) => {
  const [activeDropdown, setActiveDropdown] = useState<HTMLElement | null>(null);
  const [highlightedSubModuleId, setHighlightedSubModuleId] = useState<string | null>(null);
  const dispatch = useDispatch();

  // Get search-triggered module ID from Redux
  const searchTriggeredModuleId = useSelector((state: any) => state.Layout.searchTriggeredModuleId);

  const toggleOpen = (ref: any) => {
    if (activeDropdown === ref) {
      // ✅ Close if clicked again
      ref.classList.remove("active", "show");
      ref.nextElementSibling?.classList.add("hidden");
      setActiveDropdown(null);
      // Clear highlight when dropdown is closed
      setHighlightedSubModuleId(null);
    } else {
      activateParentDropdown(ref);
      setActiveDropdown(ref);
    }
  };

  // ✅ Close when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        activeDropdown &&
        !activeDropdown.contains(event.target as Node) &&
        !activeDropdown.nextElementSibling?.contains(event.target as Node)
      ) {
        activeDropdown.classList.remove("active", "show");
        activeDropdown.nextElementSibling?.classList.add("hidden");
        setActiveDropdown(null);
        // Clear highlight when clicking outside
        setHighlightedSubModuleId(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [activeDropdown]);

  // ✅ Auto-highlight first sub-module when opened via search
  useEffect(() => {
    if (searchTriggeredModuleId && searchTriggeredModuleId === id) {
      // This module was opened via search
      // Wait a bit for the dropdown to open, then find and highlight the first sub-module
      setTimeout(() => {
        const dropdownContent = document.querySelector(`div[main-item="${id}"] .dropdown-content`);
        if (dropdownContent && !dropdownContent.classList.contains('hidden')) {
          const firstSubModule = dropdownContent.querySelector('a[href]');
          if (firstSubModule) {
            const firstSubModuleId = firstSubModule.getAttribute('href');
            if (firstSubModuleId) {
              setHighlightedSubModuleId(firstSubModuleId);
              
              // Scroll into view
              setTimeout(() => {
                firstSubModule.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
              }, 100);
              
              // Clear the search-triggered state after the highlight is applied
              // But keep the highlight visible until user interaction
              setTimeout(() => {
                dispatch(clearSearchTriggeredModule());
              }, 500);
            }
          }
        }
      }, 200);
    }
  }, [searchTriggeredModuleId, id, dispatch]);

  const activateParentDropdown = (item: any) => {
    item.classList.add("active");
    const openDropdowns = document.querySelectorAll(".dropdown-button.active");
    const mainAttrItem = item?.closest("div[main-item]");

    openDropdowns.forEach((dropdown: any) => {
      dropdown.classList.remove("active", "show");
      const dropdownsToShow = dropdown
        ?.closest("div[main-item]")
        ?.querySelectorAll("div.dropdown-content:not(.hidden)");
      if (dropdownsToShow && dropdownsToShow.length > 0)
        dropdownsToShow.forEach((elem: any) => {
          elem.classList.add("hidden");
        });
    });

    item.classList.add("active", "show");
    if (
      !(
        document.documentElement.getAttribute("data-layout") === "vertical" &&
        document.documentElement.getAttribute("data-sidebar-size") === "sm"
      )
    )
      item.nextElementSibling?.classList.remove("hidden");

    let parentCollapseDiv = item?.closest(".dropdown-content");
    const parentDropButton = parentCollapseDiv?.parentElement.firstChild;
    parentDropButton?.classList.add("active");
    parentDropButton?.classList.add("show");
    parentDropButton?.parentElement
      ?.querySelector(".dropdown-content")
      ?.classList.remove("hidden");

    if (parentDropButton) {
      const parentParentCollapseDiv = parentDropButton?.closest(".dropdown-content");
      const parentParentDropButton = parentParentCollapseDiv?.parentElement.firstChild;
      parentParentDropButton?.classList.add("active");
      parentParentDropButton?.classList.add("show");
      parentParentDropButton?.parentElement
        ?.querySelector(".dropdown-content")
        ?.classList.remove("hidden");
    }

    mainAttrItem?.classList.add("active", "show");
    if (
      !(
        document.documentElement.getAttribute("data-layout") === "vertical" &&
        document.documentElement.getAttribute("data-sidebar-size") === "sm"
      )
    )
      mainAttrItem?.querySelector(".dropdown-content").classList.remove("hidden");
  };

  function activeOnLoad(item: any) {
    if (item?.classList.contains("dropdown-content")) {
      if (item?.parentElement) {
        const parentButton = item?.parentElement.children[0];
        parentButton?.classList.add("show");
        const mainAttrItem = item?.closest("div[main-item]");
        item?.parentElement.children[1].classList?.add("hidden");
        mainAttrItem?.querySelector(".dropdown-content")?.classList.remove("hidden");
      }
    } else {
      item?.classList.add("active");
      item?.classList.add("show");

      let parentCollapseDiv = item?.closest(".dropdown-content");
      if (
        document.documentElement.getAttribute("data-layout") === "vertical" &&
        document.documentElement.getAttribute("data-sidebar-size") !== "sm"
      ) {
        parentCollapseDiv?.classList.remove("hidden");
      }
      const parentDropButton = parentCollapseDiv?.parentElement.firstChild;
      parentDropButton?.classList.add("active");
      parentDropButton?.classList.add("show");

      const mainParentContent = parentCollapseDiv?.parentElement.closest(".dropdown-content");
      if (mainParentContent) {
        if (
          document.documentElement.getAttribute("data-layout") === "vertical" &&
          document.documentElement.getAttribute("data-sidebar-size") !== "sm"
        ) {
          mainParentContent?.classList.remove("hidden");
        }
        const mainButton = mainParentContent?.parentElement.children[0];
        mainButton?.classList.add("active", "show");
      }
    }
  }

  function removeActivation(items: any) {
    items.forEach((item: any) => {
      if (item.classList.contains("dropdown-button")) {
        const menu = item?.nextElementSibling;
        menu?.classList.add("hidden");
      }
      if (item.classList.contains("active")) {
        item.classList.remove("active");
        item.classList.remove("show");
        const parentEle = item?.closest(".dropdown-content");
        if (parentEle) {
          parentEle.classList.add("hidden");
        }
      }
    });
  }

  const currentPath = useLocation();
  const initActiveMenu = useCallback(() => {
    const publicUrl = process.env.PUBLIC_URL || "";
    // Normalize pathName: remove trailing slash
    const pathName = (publicUrl + currentPath.pathname).replace(/\/$/, "");
    
    const ul: any = document.getElementById("navbar-nav");
    const items = ul?.getElementsByTagName("a");
    if (!items) return;
    let itemsArray = [...items];
    
    // Check if the current active menu is still correct before removing activations
    // REMOVED: flawed optimization that prevented switching between siblings
    // let currentActiveMatchesPath = currentActiveItems.some(item => ...);
    
    removeActivation(itemsArray);
    
    let matchingMenuItem = null;
    let maxMatchLength = -1;

    itemsArray.forEach((x) => {
      // Get the actual href from the DOM element
      const href = x.getAttribute('href') || "";
      // Normalize menuPath: remove trailing slash and remove domain if present
      const menuPath = href.replace(/^https?:\/\/[^/]+/, "").replace(/\/$/, "");
      
      let isMatch = false;

      if (menuPath === pathName) {
        isMatch = true;
      } else if (pathName.startsWith(menuPath + "/")) {
        isMatch = true;
      }

      if (isMatch && menuPath.length > maxMatchLength) {
        maxMatchLength = menuPath.length;
        matchingMenuItem = x;
      }
    });

    if (matchingMenuItem) {
      activeOnLoad(matchingMenuItem);
    }
    
    // Clear highlight when route changes (user navigates away)
    setHighlightedSubModuleId(null);
  }, [currentPath.pathname]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
    initActiveMenu();
  }, [initActiveMenu]);

  return (
    <DropDownContext.Provider value={{ toggleOpen, setActiveDropdown, highlightedSubModuleId, setHighlightedSubModuleId }}>
      <Component main-item={id} sub-item={subitemId} className={`dropdown ${className}`}>
        {children}
      </Component>
    </DropDownContext.Provider>
  );
};

interface TriggerProps extends Omit<React.ComponentPropsWithoutRef<typeof Link>, 'type' | 'to'> {
  children: ReactNode;
  type?: ElementType | string;
  className?: string;
  id?: string;
  href?: any;
  to?: string; // Add this back if needed, or rely on hardcoded "/#"
}

export const Trigger: React.FC<TriggerProps> = ({ children, className, id, to, type: _type, ...props }) => {
  const { toggleOpen } = useContext(DropDownContext)!;
  const contentRef = useRef<any>(null);

  // Use props.to if provided, otherwise default to "/#"
  const linkTo = to || "/#";

  return (
    <Link
      ref={contentRef}
      id={id}
      to={linkTo}
      onClick={(e: any) => {
        e.preventDefault();
        toggleOpen(contentRef.current);
      }}
      className={`dropdown-button ${className}`}
      {...props}
    >
      {children}
    </Link>
  );
};

interface ContentProps {
  as?: ElementType;
  className?: string;
  children: ReactNode;
  currentPath?: any;
}

const Content: React.FC<ContentProps> = ({ as: Component = "div", className, children }) => {
  const getClassName =
    className ||
    "absolute z-50 py-2 mt-1 text-left list-none bg-white rounded-md shadow-md dropdown-menu min-w-max dark:bg-zink-400 hidden";
  return <Component className={getClassName}>{children}</Component>;
};

// SubModuleLink component that can access the highlight state
interface SubModuleLinkProps extends React.ComponentPropsWithoutRef<typeof Link> {
  children: ReactNode;
  className?: string;
  to: string;
}

export const SubModuleLink: React.FC<SubModuleLinkProps> = ({ children, className, to, onClick, ...props }) => {
  const context = useContext(DropDownContext);
  const { highlightedSubModuleId, setHighlightedSubModuleId } = context || {};
  
  // Check if this sub-module should be highlighted
  const isHighlighted = highlightedSubModuleId === to;

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    // Clear highlight when any sub-module is clicked
    if (setHighlightedSubModuleId) {
      setHighlightedSubModuleId(null);
    }
    if (onClick) {
      onClick(e);
    }
  };

  // Add highlight classes if this is the highlighted sub-module
  const highlightClasses = isHighlighted
    ? "!bg-blue-50 dark:!bg-blue-900/20"
    : "";

  return (
    <Link
      to={to}
      className={`${className} ${highlightClasses}`}
      onClick={handleClick}
      {...props}
    >
      {children}
    </Link>
  );
};

Dropdown.Trigger = Trigger;
Dropdown.Content = Content;
Dropdown.SubModuleLink = SubModuleLink;
export { Dropdown };
